import { useFormContext } from "react-hook-form";
import { StyleSheet } from "react-native";
import type {
  ReservationFormParams,
  SubscriptionQuestionParams,
} from "../../store/draftReservation/types";

import type {
  BundleAdditionalFragment,
  BundleQuestionCategoryFragment,
  BundleQuestionFragment,
} from "@stepapp/core";
import { Block, SvgIcon, Typography, palette, spacing } from "@stepapp/ui";

import BasicQuestions from "./BasicQuestions";
import CategoryQuestions from "./CategoryQuestions";
import PeriodicityQuestions from "./PeriodicityQuestions";
import QuantityQuestions from "./QuantityQuestions";
import { CategoryType } from "@stepapp/core/apiTypes";

export type QuantityAnswerType = {
  qaQuantityId: string;
  qaQuantityAdditional?: { id: string; price: number };
  qaQuantitySelection: { id: string; price: number; quantity: number }[];
};

type Props = {
  categoryQuestions: BundleQuestionCategoryFragment[];
  basicQuestions: BundleQuestionFragment[];
  quantityQuestions: BundleAdditionalFragment[];
  subscriptionQuestion: SubscriptionQuestionParams;
  additionalServiceTitle: string;
  additionalServiceDescription: string;
  showCustomAdditionalDetails: boolean;
  categoryType: CategoryType;
};

const Questions = ({
  categoryQuestions,
  basicQuestions,
  quantityQuestions,
  subscriptionQuestion,
  additionalServiceTitle,
  additionalServiceDescription,
  showCustomAdditionalDetails,
  categoryType,
}: Props) => {
  const { watch } = useFormContext<ReservationFormParams>();
  const services = watch("services");

  const disclaimers = services.map((service) => {
    if (service?.variants.some((variant) => (variant?.quantity || 0) > 0)) {
      const matchedQuestion = quantityQuestions.find(
        (question) => question.id === service?.serviceId,
      );
      return (
        matchedQuestion && {
          id: matchedQuestion.id,
          description: matchedQuestion.additionalDescription,
        }
      );
    }
    return null;
  });

  return (
    <>
      <PeriodicityQuestions question={subscriptionQuestion} />

      <CategoryQuestions categoryQuestions={categoryQuestions} categoryType={categoryType} />

      <BasicQuestions questions={basicQuestions} />

      <QuantityQuestions
        questions={quantityQuestions}
        additionalServiceTitle={additionalServiceTitle}
        additionalServiceDescription={additionalServiceDescription}
        showCustomAdditionalDetails={showCustomAdditionalDetails}
      />

      <Block justifyCenter marginBottom={disclaimers.length > 0 ? 6 : 0}>
        {disclaimers.filter(Boolean).map((disclaimer) => (
          <Block row key={disclaimer?.id} marginTop={1}>
            <Block justifyCenter row marginRight={1}>
              <Block borderRadius={5} height={2.5} style={styles.content} width={2.5} />
              <SvgIcon color={palette.primary.main} name="alert-filled" size={25} />
            </Block>
            <Typography variant="lightLabel">{disclaimer?.description}</Typography>
          </Block>
        ))}
      </Block>
    </>
  );
};

export default Questions;

const styles = StyleSheet.create({
  content: {
    backgroundColor: palette.primary.accent,
    marginTop: spacing.unit * 0.5,
    position: "absolute",
  },
});
